import { HttpErrorResponse } from '@angular/common/http';
import type { ApiErrorBody, ApiValidationErrors } from '../models/http-error-body.model';

const MAX_MESSAGE_LENGTH = 280;

const GENERIC_PROBLEM_TITLES = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'method not allowed',
  'conflict',
  'unprocessable entity',
  'too many requests',
  'internal server error',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
  'one or more validation errors occurred.',
  'one or more validation errors occurred',
]);

export const HTTP_ERROR_I18N_KEYS = {
  network: 'common.errors.network',
  forbidden: 'common.errors.forbidden',
  notFound: 'common.errors.notFound',
  conflict: 'common.errors.conflict',
  validation: 'common.errors.validation',
  tooManyRequests: 'common.errors.tooManyRequests',
  server: 'common.errors.server',
  generic: 'common.errors.generic',
} as const;

export const HTTP_ERROR_FALLBACKS: Record<
  (typeof HTTP_ERROR_I18N_KEYS)[keyof typeof HTTP_ERROR_I18N_KEYS],
  string
> = {
  [HTTP_ERROR_I18N_KEYS.network]:
    'Unable to reach the server. Check your connection and try again.',
  [HTTP_ERROR_I18N_KEYS.forbidden]: 'You do not have permission to perform this action.',
  [HTTP_ERROR_I18N_KEYS.notFound]: 'The requested resource was not found.',
  [HTTP_ERROR_I18N_KEYS.conflict]:
    'This action conflicts with existing data. Refresh and try again.',
  [HTTP_ERROR_I18N_KEYS.validation]: 'Some fields are invalid. Review your input and try again.',
  [HTTP_ERROR_I18N_KEYS.tooManyRequests]: 'Too many requests. Please wait a moment and try again.',
  [HTTP_ERROR_I18N_KEYS.server]: 'Something went wrong on our side. Please try again later.',
  [HTTP_ERROR_I18N_KEYS.generic]: 'Something went wrong. Please try again.',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return isRecord(value);
}

function sanitizeMessage(message: string): string | null {
  const normalized = message.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return null;
  }
  if (/<[a-z][\s\S]*>/i.test(normalized)) {
    return null;
  }
  if (normalized.length > MAX_MESSAGE_LENGTH) {
    return `${normalized.slice(0, MAX_MESSAGE_LENGTH - 1)}…`;
  }
  return normalized;
}

function isGenericProblemTitle(title: string): boolean {
  return GENERIC_PROBLEM_TITLES.has(title.trim().toLowerCase());
}

function collectValidationMessages(errors: ApiValidationErrors | readonly string[]): string[] {
  if (Array.isArray(errors)) {
    return errors.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  }

  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      messages.push(value.trim());
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim().length > 0) {
          messages.push(item.trim());
        }
      }
    }
  }
  return messages;
}

/** Extracts a user-facing message from a typical API error body. */
export function extractApiErrorMessage(body: unknown): string | null {
  if (typeof body === 'string') {
    return sanitizeMessage(body);
  }

  if (!isApiErrorBody(body)) {
    return null;
  }

  const candidates: Array<string | undefined> = [
    body.message,
    body.detail,
    body.error_description,
    body.errorMessage,
    body.data?.message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const sanitized = sanitizeMessage(candidate);
      if (sanitized) {
        return sanitized;
      }
    }
  }

  if (body.errors) {
    const validationMessages = collectValidationMessages(body.errors);
    if (validationMessages.length > 0) {
      const summary = validationMessages.slice(0, 3).join(' ');
      const sanitized = sanitizeMessage(summary);
      if (sanitized) {
        return sanitized;
      }
    }
  }

  if (typeof body.title === 'string') {
    const sanitized = sanitizeMessage(body.title);
    if (sanitized && !isGenericProblemTitle(sanitized)) {
      return sanitized;
    }
  }

  if (typeof body.error === 'string' && body.error.trim().length > 0) {
    const sanitized = sanitizeMessage(body.error);
    if (sanitized && sanitized !== 'invalid_grant') {
      return sanitized;
    }
  }

  return null;
}

export function isAbortedRequest(error: HttpErrorResponse): boolean {
  if (error.status !== 0) {
    return false;
  }

  const payload = error.error;
  if (payload instanceof DOMException && payload.name === 'AbortError') {
    return true;
  }

  if (isRecord(payload) && payload['name'] === 'AbortError') {
    return true;
  }

  return error.message.toLowerCase().includes('abort');
}

/** Resolves the i18n key for a status-based fallback when the API provides no safe message. */
export function resolveHttpErrorI18nKey(status: number): (typeof HTTP_ERROR_I18N_KEYS)[keyof typeof HTTP_ERROR_I18N_KEYS] {
  if (status === 403) {
    return HTTP_ERROR_I18N_KEYS.forbidden;
  }
  if (status === 404) {
    return HTTP_ERROR_I18N_KEYS.notFound;
  }
  if (status === 409) {
    return HTTP_ERROR_I18N_KEYS.conflict;
  }
  if (status === 422 || status === 400) {
    return HTTP_ERROR_I18N_KEYS.validation;
  }
  if (status === 429) {
    return HTTP_ERROR_I18N_KEYS.tooManyRequests;
  }
  if (status >= 500) {
    return HTTP_ERROR_I18N_KEYS.server;
  }
  return HTTP_ERROR_I18N_KEYS.generic;
}

const I18N_KEY_PATTERN = /^[a-z][a-z0-9]*(?:\.[a-zA-Z][\w-]*)+$/;

/** True when a string looks like a translation key (e.g. common.errors.generic). */
export function looksLikeTranslationKey(message: string): boolean {
  return I18N_KEY_PATTERN.test(message.trim());
}

export function resolveHttpErrorMessage(error: HttpErrorResponse): string {
  if (error.status === 0) {
    return HTTP_ERROR_FALLBACKS[HTTP_ERROR_I18N_KEYS.network];
  }

  const apiMessage = extractApiErrorMessage(error.error);
  if (apiMessage && !looksLikeTranslationKey(apiMessage)) {
    return apiMessage;
  }

  const statusKey = resolveHttpErrorI18nKey(error.status);
  return HTTP_ERROR_FALLBACKS[statusKey];
}

export function isStaticAssetRequest(url: string): boolean {
  try {
    return url.includes('/assets/');
  } catch {
    return false;
  }
}

/** Login, refresh, and other auth calls that must surface 401 to the user. */
export function isAuthApiRequest(url: string): boolean {
  try {
    return url.includes('/api/auth/');
  } catch {
    return false;
  }
}

export function shouldShowGlobalErrorToast(
  error: HttpErrorResponse,
  options: { skipToast: boolean; url: string },
): boolean {
  if (options.skipToast || isStaticAssetRequest(options.url)) {
    return false;
  }
  if (isAbortedRequest(error)) {
    return false;
  }
  // Session 401s: auth interceptor refreshes or redirects without a toast.
  // Auth API 401s (e.g. invalid login) must still notify the user.
  if (error.status === 401 && !isAuthApiRequest(options.url)) {
    return false;
  }
  return true;
}
